import { DialogContent, DialogContentText } from '@mui/material';
import { MethodType } from 'components/DropDown/MethodType';
import DxPopupModal from 'components/hoc/PopupModal/DxPopupModal';
import ModalContent from 'components/hoc/PopupModal/ModalContent';
import { ComponentType } from 'components/hoc/PopupModal/ModalEnum';
import ModalFooter from 'components/hoc/PopupModal/ModalFooter';
import ModalHeader from 'components/hoc/PopupModal/ModalHeader';
import { AlertModal } from 'components/Modals/AlertModal/AlertModal';
import DOMPurify from 'dompurify';
import 'components/Modals/DeleteModal/DeleteModal.css';
import {
  dbConstActionMessage,
  dbConstActionMessageCode,
  dbConstOperationDetails,
  dbConstOperationMessage,
  dbConstOperationPrimaryKey,
  dbConstOperationResultCode,
} from 'constants/DbConstants';
import { deleteData, postData } from 'data/DataConnector';
import { ScrollView } from 'devextreme-react';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { getErrorMessage, isEqualProps, isStringNullOrWhitespace } from 'util/helpers/helperFunctions';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';

export interface IProps {
  isModalOpen: boolean;
  isDtoRequired: boolean;
  header?: string;
  message?: string;
  methodRoute: string;
  parameters: any;
  onCloseModal: any;
  onDeleteData: any;
  username: string;
  confirmationRequired?: boolean;
  methodType?: MethodType;
  deleteAlertMessage?: string;
}

interface IState {
  isLoading: boolean;
  showDelete: boolean;
  showAlert: boolean;
  alertMessage: string;
  showConfirmation: boolean;
  confirmationMessage: string[];
}

const DeleteModal: React.FC<IProps> = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(props.isModalOpen);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string[]>([]);
  const closeDeleteModalRef = useRef(true);

  useEffect(() => {
    setShowDelete(props.isModalOpen);
  }, [props.isModalOpen]);

  const onModalFooterDelete = () => {
    onDelete(
      props.methodRoute,
      props.parameters,
      props.confirmationRequired === true ? true : false,
    );
  };

  const onConfirmedClicked = () => {
    onDelete(props.methodRoute, props.parameters, false);
  };

  const showAlertModal = (message: string) => {
    setAlertMessage(message);
    setIsLoading(false);
    setShowAlert(true);
    setShowDelete(!closeDeleteModalRef.current);
  };

  const closeAlertModal = () => {
    setIsLoading(false);
    setShowAlert(false);
    setShowConfirmation(false);
    if (closeDeleteModalRef.current) {
      props.onCloseModal();
    }
  };

  const onDelete = (methodRoute: string, parameters: any, confirmationRequired: boolean) => {
    if (props.isDtoRequired) {
      parameters = {
        ...parameters,
        operationConfirmed: confirmationRequired === true ? 0 : 1,
        silentMode: 0,
        userId: props.username,
      };
    } else {
      parameters += `&operationConfirmed=${confirmationRequired === true ? 0 : 1}`;
    }

    setIsLoading(true);
    setShowConfirmation(false);

    if (props.methodType === MethodType.Post) {
      postData(methodRoute, parameters)
        .then(response => {
          validateResponse(response, confirmationRequired);
        })
        .catch(error => {
          showAlertModal(getErrorMessage(error));
        });
    } else {
      deleteData(methodRoute, parameters, props.isDtoRequired)
        .then(response => {
          validateResponse(response, confirmationRequired);
        })
        .catch(error => {
          showAlertModal(getErrorMessage(error));
        });
    }
  };

  const validateResponse = (response: any, confirmationRequired: boolean) => {
    if (JSON.parse(response).recordSets[0].data && JSON.parse(response).recordSets[0].data.length > 0) {
      const operationResultCode = JSON.parse(response).recordSets[0].data[0][dbConstOperationResultCode];
      const operationMessage = JSON.parse(response).recordSets[0].data[0][dbConstOperationMessage];
      const operationDetails = JSON.parse(response).recordSets[0].data[0][dbConstOperationDetails];
      const operationPrimaryKey = JSON.parse(response).recordSets[0].data[0][dbConstOperationPrimaryKey];

      if (operationResultCode >= 10 || !isStringNullOrWhitespace(operationMessage)) {
        let message = operationMessage;
        if (message === undefined || message === null) {
          message = '';
        }
        if (!isStringNullOrWhitespace(operationDetails)) {
          message += '\n' + operationDetails;
        }

        const actionMessageCode = JSON.parse(response).recordSets[0].data[0][dbConstActionMessageCode];
        const actionMessage = JSON.parse(response).recordSets[0].data[0][dbConstActionMessage];

        if (confirmationRequired && !isStringNullOrWhitespace(actionMessageCode)) {
          switch (actionMessageCode) {
            case 'ARE_YOU_SURE':
              setConfirmationMessage([operationMessage, operationDetails]);
              setIsLoading(false);
              setShowAlert(false);
              setShowConfirmation(true);
              break;
            case 'DEL_NOT_ALLOWED':
              if (operationMessage) {
                message = operationMessage;
              }
              message += actionMessage
                ? (operationDetails ? '\n' + operationDetails : '') + '\n\n' + actionMessage
                : '\n\nDelete operation is not allowed.';
              closeDeleteModalRef.current = false;
              showAlertModal(message);
              break;
            case 'DELETE_NOT_ALLOWED':
              if (operationMessage) {
                message = operationMessage;
              }
              message += actionMessage
                ? (operationDetails ? '\n' + operationDetails : '') + '\n\n' + actionMessage
                : '\n\nDelete operation is not allowed.';
              closeDeleteModalRef.current = false;
              showAlertModal(message);
              break;
            case 'CHECK_TRY_AGAIN':
              if (operationMessage) {
                message = operationMessage;
              }
              message += '\n\nPlease check your data and try again';
              closeDeleteModalRef.current = false;
              showAlertModal(message);
              break;
            case 'OPERATION_ABORTED':
              if (operationMessage) {
                message = operationMessage;
              }
              message += actionMessage ? '\n\n' + actionMessage : '';
              closeDeleteModalRef.current = false;
              showAlertModal(message);
              break;
          }

          return;
        } else {
          showAlertModal(message);
        }
      } else {
        setIsLoading(false);
        setShowAlert(false);
        setShowDelete(false);
        props.onDeleteData(operationPrimaryKey);
      }
    } else {
      setIsLoading(false);
      setShowAlert(false);
      setShowDelete(false);
      props.onDeleteData();
    }
  };

  return (
      <React.Fragment>
        <AlertModal
          isModalOpen={showAlert}
          alertMessage={alertMessage}
          onCloseModal={closeAlertModal}
        />

        <DxPopupModal
          isModalOpen={showDelete}
          modalHeader={
            <ModalHeader
              isComponentLoading={isLoading}
              componentHeader="Are you sure?"
              onCloseClicked={props.onCloseModal}
              componentType={ComponentType.Delete}
            />
          }
        >
          <ScrollView
            scrollByContent={true}
            bounceEnabled={false}
            direction={'both'}
            showScrollbar={'always'}
            scrollByThumb={true}
          >
            <DialogContent style={{ padding: '0px' }}>
              <DialogContentText
                component={'div'}
                style={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'normal',
                }}
              >
                <div className="delete-modal-content">
                  <ModalContent isLoading={isLoading}>
                    {
                      <React.Fragment>
                        {props.deleteAlertMessage ? <b><span style={{  display: 'block' }}>{props.deleteAlertMessage}</span></b> : ''}
                        <span>
                          {props.message ? (
                            <b
                              dangerouslySetInnerHTML={{
                                __html:DOMPurify.sanitize('You are about to delete ' + props.message,)
                              }}
                            />
                          ) : (
                            <b>You are about to remove information.</b>
                          )}
                        </span>
                        <span>Are you sure you want to continue?</span>
                      </React.Fragment>
                    }
                  </ModalContent>
                </div>
              </DialogContentText>
            </DialogContent>
            <br />
            <br />
          </ScrollView>
          <div>
            <ModalFooter
              isComponentLoading={isLoading}
              onCancelClicked={props.onCloseModal}
              onOperatioConfirmed={onModalFooterDelete}
              componentType={ComponentType.Delete}
            />
          </div>
        </DxPopupModal>

        <ConfirmationModal
          isModalOpen={showConfirmation}
          messages={confirmationMessage}
          onCloseModal={closeAlertModal}
          onConfirm={onConfirmedClicked}
        />
      </React.Fragment>
    );
};

const mapStateToProps = (stateFromStore: any) => {
  return {
    username: stateFromStore.globalRdcr.username,
  };
};

const ConnectedDeleteModal = connect(mapStateToProps)(DeleteModal);

export { ConnectedDeleteModal as DeleteModal };
